import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import OrderCard from './OrderCard';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import { IOrder, OrderHistoryResponse, IPagination } from './types';
import { LoadingMoreSpinner } from '../../../product-listing-page/components/SmallUIComponents';
import TablePagination from '@material-ui/core/TablePagination';
import ErrorUI from './ErrorUI';
import { aloApiAuthFetch } from '../../../../utils/alo-api-fetch';

const urlParams = InitialUrlSearchParams;
const DEFAULT_ORDER_COUNT = 5;
const LIMIT_ORDER_COUNT_PER_PAGE = 15;

const OrderHistory = () => {
  const [orders, setOrders] = useState<IOrder[] | null>(null);
  const [isError, setIsError] = useState(false);
  const [paginationData, setPaginationData] = useState<IPagination | null>(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setPage] = useState(Number(urlParams.get('page')) || 1);
  const SALES_CHANNEL: 'all' | 'online' | 'in_store' = 'all';
  const orderCountFromURLparams = Number(urlParams.get('count'));
  const adjustedOrderCount =
    orderCountFromURLparams < LIMIT_ORDER_COUNT_PER_PAGE ? orderCountFromURLparams : LIMIT_ORDER_COUNT_PER_PAGE; // Limit possible user input to 15 to avoid big requests
  const [orderCount, setOrderCount] = useState(adjustedOrderCount || DEFAULT_ORDER_COUNT);
  const paginationRef = useRef({ startCursor: '', endCursor: '', direction: '' });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      const currentPaginationData = paginationRef.current;
      const cursor =
        currentPaginationData.direction && currentPaginationData.direction === 'next'
          ? `&end_cursor=${currentPaginationData.endCursor}`
          : currentPaginationData.direction
          ? `&start_cursor=${currentPaginationData.startCursor}`
          : '';

      aloApiAuthFetch(`/v1/orders?page=${currentPage}&page_size=${orderCount}&channel=${SALES_CHANNEL}${cursor}`)
        .then((res) => res.json())
        .then((response: OrderHistoryResponse) => {
          const error = response.detail;
          if (error) {
            setIsError(true);
            console.log('Alo API error: ', error);
          } else {
            if (response.pagination?.start_cursor || response.pagination?.end_cursor)
              paginationRef.current = {
                ...paginationRef.current,
                startCursor: response.pagination.start_cursor || '',
                endCursor: response.pagination.end_cursor || '',
              };
            response.orders ? setOrders(response.orders) : setIsError(true);
            if (response.pagination) {
              setPaginationData(response.pagination);
            }
          }
          setLoading(false);
        });
    };
    fetchOrders();
  }, [orderCount, currentPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    paginationRef.current = { ...paginationRef.current, direction: currentPage > newPage + 1 ? 'prev' : 'next' };

    setPage(newPage + 1);
    urlParams.set('page', (newPage + 1).toString());
    window.history.replaceState({}, '', window.location.origin + window.location.pathname + '?' + urlParams.toString());
  };

  const [rowsPerPage, setRowsPerPage] = React.useState(orderCount);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
    setOrderCount(newValue);
    setPage(1);
    urlParams.set('page', '1');
    urlParams.set('count', newValue.toString());
    window.history.replaceState({}, '', window.location.origin + window.location.pathname + '?' + urlParams.toString());
  };

  if (isError) return <ErrorUI />;
  if (loading || !orders) return <LoadingMoreSpinner />;
  if (orders.length === 0)
    return (
      <div className="col-12 col-md-9">
        <p className="body mt-xl-5 text-center">You haven't placed any orders yet.</p>
      </div>
    );

  return (
    <>
      <div className="order-history-wrapper">
        {orders?.map((order) => (
          <OrderCard order={order} />
        ))}
      </div>
      {paginationData?.total_pages && paginationData.total_pages > 1 ? (
        <TablePagination
          component="div"
          count={paginationData.total_items}
          page={currentPage - 1}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage="Orders per page:"
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={Array.from(new Set([5, 10, 15, rowsPerPage])).sort((a, b) => a - b)}
        />
      ) : null}
    </>
  );
};

export default OrderHistory;

const renderOrderHistory = () => {
  const mountNode = document.querySelector('[react-render-target="order-history"]');
  if (mountNode) ReactDOM.render(<OrderHistory />, mountNode);
  else console.warn('missing render target: order-history');
};

export const InitOrderHistory = () => {
  renderOrderHistory();
};
